<script setup lang="ts">
import axios from 'axios';
import { ref, computed } from 'vue';
import { message } from 'ant-design-vue';
import type { FormInstance, FormProps } from 'ant-design-vue';
import solcentreApiClient from '@/http/axios';
import api from '@/http/api';
import { AppContext } from '@/models/AppContext';
import { useAuthStore } from '@/shared/stores/authStore';
import { getAuthToken, refreshCognitoSession } from '@/shared/auth/session';
import {
  mapCognitoErrorToMessage,
  updateUserEmail,
  verifyUserEmail,
} from '@/shared/auth/cognitoUserAttributes';
import { validateEmailAddress } from '@/utils/validators';

enum ChangeEmailStep {
  EnterEmail = 'enter-email',
  Verify = 'verify',
}

const authStore = useAuthStore();
const currentEmail = computed(() =>
  authStore.profileEmail(AppContext.SolCentre).trim().toLowerCase(),
);

const step = ref<ChangeEmailStep>(ChangeEmailStep.EnterEmail);
const isSubmitting = ref(false);
const isVerifying = ref(false);
const isResendingCode = ref(false);
const pendingNewEmail = ref('');

const emailForm = ref({ newEmail: '' });
const emailFormRef = ref<FormInstance>();
const emailFieldCustomError = ref('');
const verifyForm = ref({ code: '' });
const verificationCodeFieldError = ref('');

const ERROR_MESSAGES = {
  sessionExpired: 'Your session has expired. Please sign in again.',
  unavailableEmail: 'This email cannot be used. Please choose a different email address.',
  sameEmailEntered: 'Please enter a different email address than your current one.',
  somethingWentWrong: 'Something went wrong. Please contact Allume Support.',
};

const verificationInfoMessage = computed(() => {
  const inboxAddress = pendingNewEmail.value ? pendingNewEmail.value : 'your new email inbox';
  return `Check ${inboxAddress} for a verification code. You can still sign in with your previous email until verification succeeds.`;
});

function onEmailInputChange(value: string) {
  emailForm.value.newEmail = value.toLowerCase().trim();
  emailFieldCustomError.value = '';
  emailFormRef.value?.clearValidate(['newEmail']);
}

function validateDifferentEmail(_rule: unknown, value: string): Promise<void> {
  if (!value) {
    return Promise.resolve();
  } else {
    const enteredEmail = value.trim().toLowerCase();
    return enteredEmail === currentEmail.value ? Promise.reject() : Promise.resolve();
  }
}

const emailRules = {
  newEmail: [
    { required: true, message: 'New email is required.' },
    { validator: validateEmailAddress, trigger: 'blur' },
    {
      validator: validateDifferentEmail,
      trigger: 'blur',
      message: ERROR_MESSAGES.sameEmailEntered,
    },
  ],
} as FormProps['rules'];

const verificationCodeRules = {
  code: [{ required: true, message: 'Verification code is required.' }],
} as FormProps['rules'];

async function submitNewEmail(values: { newEmail: string }) {
  isSubmitting.value = true;
  const enteredEmail = values.newEmail.trim().toLowerCase();

  try {
    const token = await getAuthToken();
    if (!token) return message.error(ERROR_MESSAGES.sessionExpired);

    const isAvailable = await checkEmailAvailability(enteredEmail);
    if (!isAvailable) {
      emailFieldCustomError.value = ERROR_MESSAGES.unavailableEmail;
      return;
    }

    await sendVerificationCode(token, enteredEmail);
  } catch (e) {
    console.error('Error submitting new email:', e);
    if (axios.isAxiosError(e)) {
      message.error(e.response?.data?.message ?? ERROR_MESSAGES.somethingWentWrong);
    } else {
      message.error(mapCognitoErrorToMessage(e));
    }
  } finally {
    isSubmitting.value = false;
  }
}

async function checkEmailAvailability(email: string): Promise<boolean> {
  const { data } = await solcentreApiClient.post(api.checkEmailAvailability, { email });
  return data.available;
}

async function sendVerificationCode(token: string, enteredEmail: string) {
  await updateUserEmail(token, enteredEmail);
  pendingNewEmail.value = enteredEmail;
  step.value = ChangeEmailStep.Verify;
  verifyForm.value.code = '';
  verificationCodeFieldError.value = '';
}

async function submitVerificationCode(values: { code: string }) {
  isVerifying.value = true;

  try {
    const token = await getAuthToken();
    if (!token) return message.error(ERROR_MESSAGES.sessionExpired);

    await verifyUserEmail(token, values.code);
    await refreshCognitoSession();
    message.success('Email updated successfully.', 5);
    resetForm();
  } catch (e) {
    verificationCodeFieldError.value = mapCognitoErrorToMessage(e);
  } finally {
    isVerifying.value = false;
  }
}

function resetForm() {
  step.value = ChangeEmailStep.EnterEmail;
  pendingNewEmail.value = '';
  emailForm.value.newEmail = '';
  verifyForm.value.code = '';
  emailFieldCustomError.value = '';
  verificationCodeFieldError.value = '';
  emailFormRef.value?.clearValidate(['newEmail']);
}

function onVerifyCodeChange(value: string) {
  verifyForm.value.code = value;
  verificationCodeFieldError.value = '';
}

async function resendVerificationCode() {
  const email = pendingNewEmail.value.trim().toLowerCase();
  if (!email) {
    resetForm();
    return;
  }

  isResendingCode.value = true;
  try {
    const token = await getAuthToken();
    if (!token) {
      message.error(ERROR_MESSAGES.sessionExpired);
      return;
    }
    await sendVerificationCode(token, email);
    message.success('Verification code sent.', 5);
  } catch (e) {
    message.error(mapCognitoErrorToMessage(e));
  } finally {
    isResendingCode.value = false;
  }
}
</script>

<template>
  <div class="change-email-block">
    <p>Current email: {{ currentEmail }}</p>

    <template v-if="step === ChangeEmailStep.EnterEmail">
      <a-form ref="emailFormRef" :model="emailForm" :rules="emailRules" @finish="submitNewEmail">
        <a-form-item
          name="newEmail"
          extra="You will receive a verification code at the new address. Until you verify, sign in with your current email."
          validateFirst
          :validate-status="emailFieldCustomError ? 'error' : undefined"
          :help="emailFieldCustomError || undefined"
        >
          <a-input
            :value="emailForm.newEmail"
            placeholder="New email"
            @update:value="onEmailInputChange"
          />
        </a-form-item>
        <a-button :loading="isSubmitting" type="primary" html-type="submit">
          Send verification code
        </a-button>
      </a-form>
    </template>

    <template v-else>
      <a-alert
        message="Verification code sent"
        :description="verificationInfoMessage"
        type="info"
        show-icon
        class="verification-alert"
      />
      <a-form :model="verifyForm" :rules="verificationCodeRules" @finish="submitVerificationCode">
        <a-form-item
          name="code"
          label="Verification code"
          validateFirst
          :validate-status="verificationCodeFieldError ? 'error' : undefined"
          :help="verificationCodeFieldError || undefined"
        >
          <a-input
            :value="verifyForm.code"
            placeholder="Code"
            autocomplete="one-time-code"
            @update:value="onVerifyCodeChange"
          />
        </a-form-item>
        <a-space wrap>
          <a-button :loading="isVerifying" type="primary" html-type="submit">
            Verify and update email
          </a-button>
          <a-button
            type="default"
            html-type="button"
            :loading="isResendingCode"
            @click="resendVerificationCode"
          >
            Resend code
          </a-button>
          <a-button type="link" html-type="button" @click="resetForm"
            >Use a different email</a-button
          >
        </a-space>
      </a-form>
    </template>
  </div>
</template>

<style scoped>
.change-email-block {
  width: 400px;
}

.verification-alert {
  margin-bottom: 16px;
}
</style>
